import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Card surface — see STYLING_GUIDE.md → "Card surface standard".
 *
 * Recipe (both modes auto-applied):
 *   Light mode: bg-card + border + shadow-card + lighter top edge ("light from above")
 *   Dark mode:  bg-card-gradient + border + shadow-card + lighter top edge
 *   Hover (when hoverable): border-primary + shadow-card-hover + lift
 *
 * Polymorphic via `as` prop — pass Link, button, etc. Defaults to div.
 */
const Card = React.forwardRef(({ as: Component = "div", className, hoverable, ...props }, ref) => (
  <Component
    ref={ref}
    className={cn(
      "rounded-xl bg-card text-card-foreground border border-border border-t-highlight shadow-card dark:bg-card-gradient",
      hoverable &&
        "cursor-pointer transition-[border-color,box-shadow,transform] duration-150 hover:border-primary hover:shadow-card-hover hover:-translate-y-px",
      className,
    )}
    {...props}
  />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef(({ className, ...props }, ref) => <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef(({ className, ...props }, ref) => <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef(({ className, ...props }, ref) => <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
